/**
 * Per-team (client) intake settings — SPA-side types.
 *
 * Mirrors vnext-api/vnext/schemas/team_settings.py. The intake mode
 * shapes the Request Report page: which report types a client sees and
 * what free-text submission does (classify → enquiry vs deterministic
 * screening order).
 */

export type IntakeMode =
  | 'custom_configs'
  | 'screening_and_custom'
  | 'screening_only'
  | 'enquiry_only';

/** Sentinel id for the synthetic "Screening" picker entry. Screening is
 *  not a report config — a screening order is a config-less request —
 *  so the pick is translated to `report_config_id: null` at the API
 *  boundary (with `screening_pinned` on extract). */
export const SCREENING_CONFIG_ID = '__screening__';

export interface ResolvedIntake {
  teamId: string;
  /** The stored (or default) mode. */
  intakeMode: IntakeMode;
  /** What the API enforces once the visible catalogue is folded in
   *  (custom_configs with nothing visible behaves as enquiry_only). */
  effectiveMode: IntakeMode;
  /** Whether the compose page offers Screening. */
  offersScreening: boolean;
  hasCustomConfigs: boolean;
}

export interface TeamSettingsRow {
  teamId: string;
  intakeMode: IntakeMode;
}

// --- API (snake_case) mirrors ---

export interface ApiResolvedIntake {
  team_id: string;
  intake_mode: IntakeMode;
  effective_mode: IntakeMode;
  offers_screening: boolean;
  has_custom_configs: boolean;
}

export interface ApiTeamSettingsRow {
  team_id: string;
  intake_mode: IntakeMode;
}

export function transformToResolvedIntake(api: ApiResolvedIntake): ResolvedIntake {
  return {
    teamId: api.team_id,
    intakeMode: api.intake_mode,
    effectiveMode: api.effective_mode,
    offersScreening: api.offers_screening,
    hasCustomConfigs: api.has_custom_configs,
  };
}

export function transformToTeamSettingsRow(api: ApiTeamSettingsRow): TeamSettingsRow {
  return { teamId: api.team_id, intakeMode: api.intake_mode };
}

export const INTAKE_MODE_OPTIONS: { value: IntakeMode; label: string }[] = [
  { value: 'custom_configs', label: 'Custom report configs' },
  { value: 'screening_and_custom', label: 'Screening + custom report configs' },
  { value: 'screening_only', label: 'Screening only' },
  { value: 'enquiry_only', label: 'Enquiry only' },
];

// --- Notification recipients (who is emailed about which notification) ---
//
// Mirrors vnext-api/vnext/schemas/team_settings.py. A team has an optional
// default rule and optional per-notification overrides; each rule names a base
// audience and may add one confirmed copy address. A team with no rules follows
// the global default, which the API reports so the UI can say so.

/** The audience a notification goes to. `off` sends nothing at all. */
export type RecipientMode = 'team' | 'project' | 'selected' | 'none' | 'off';

export const RECIPIENT_MODE_OPTIONS: {
  value: RecipientMode;
  label: string;
  description: string;
}[] = [
  {
    value: 'team',
    label: 'Everyone on the team',
    description: 'Every client user on the team.',
  },
  {
    value: 'project',
    label: 'Report creator and project members',
    description:
      'The person who created the report plus the people on its project. Until a project ' +
      'exists (a new report, or one awaiting input) only the creator is told.',
  },
  {
    value: 'selected',
    label: 'Selected people',
    description:
      'Only the people chosen below. If nobody chosen can be reached, the report’s creator ' +
      'is told instead.',
  },
  {
    value: 'none',
    label: 'No individuals',
    description:
      'Nobody personally. Use this with a copy address, for a shared inbox only. If there is ' +
      'no copy address, the report’s creator is told instead.',
  },
  {
    value: 'off',
    label: 'Don’t send',
    description: 'Nobody is emailed about this notification — not even the report’s creator.',
  },
];

export const RECIPIENT_MODE_LABELS: Record<RecipientMode, string> = Object.fromEntries(
  RECIPIENT_MODE_OPTIONS.map((o) => [o.value, o.label])
) as Record<RecipientMode, string>;

/** Path segment for the team-wide default rule, as opposed to one notification's. */
export const DEFAULT_RULE_KEY = 'default';

/** A notification a team can choose recipients for. Served by the API, so a new
 *  notification appears here without a frontend change. */
export interface NotificationEvent {
  type: string;
  label: string;
  description: string;
}

export interface NotificationRule {
  /** `*` for the team default, otherwise a notification's dotted type. */
  eventType: string;
  baseMode: RecipientMode;
  selectedUserIds: number[];
  /** Also tell the report's creator, on top of `selectedUserIds`. `selected` only. */
  includeCreator: boolean;
  copyAddress: string | null;
}

/** What is sent to save a rule. */
export interface NotificationRuleInput {
  baseMode: RecipientMode;
  selectedUserIds: number[];
  includeCreator: boolean;
  copyAddress: string | null;
}

/** `pending` has a live link outstanding; `expired` needs resending. Neither
 *  receives anything until confirmed. */
export type AddressStatus = 'verified' | 'pending' | 'expired';

export interface NotificationAddress {
  id: string;
  email: string;
  status: AddressStatus;
  verifiedAt: string | null;
}

export interface TeamNotifications {
  teamId: string;
  /** What a notification with no rule of its own falls back to. */
  globalDefaultMode: RecipientMode;
  defaultRule: NotificationRule | null;
  overrides: NotificationRule[];
  addresses: NotificationAddress[];
}

export interface TeamMember {
  id: number;
  name: string;
  email: string | null;
  /** They have chosen to hear only about reports they created themselves, which
   *  being selected does not override. */
  isMutedTeamProjects: boolean;
}

export interface ApiNotificationRule {
  event_type: string;
  base_mode: RecipientMode;
  selected_user_ids: number[];
  include_creator: boolean;
  copy_address: string | null;
}

export interface ApiNotificationAddress {
  id: string;
  email: string;
  status: AddressStatus;
  verified_at: string | null;
}

export interface ApiTeamNotifications {
  team_id: string;
  global_default_mode: RecipientMode;
  default_rule: ApiNotificationRule | null;
  overrides: ApiNotificationRule[];
  addresses: ApiNotificationAddress[];
}

export interface ApiTeamMember {
  id: number;
  name: string;
  email: string | null;
  is_muted_team_projects: boolean;
}

export function transformToNotificationRule(api: ApiNotificationRule): NotificationRule {
  return {
    eventType: api.event_type,
    baseMode: api.base_mode,
    selectedUserIds: api.selected_user_ids,
    includeCreator: api.include_creator,
    copyAddress: api.copy_address,
  };
}

export function transformToNotificationAddress(api: ApiNotificationAddress): NotificationAddress {
  return { id: api.id, email: api.email, status: api.status, verifiedAt: api.verified_at };
}

export function transformToTeamNotifications(api: ApiTeamNotifications): TeamNotifications {
  return {
    teamId: api.team_id,
    globalDefaultMode: api.global_default_mode,
    defaultRule: api.default_rule ? transformToNotificationRule(api.default_rule) : null,
    overrides: api.overrides.map(transformToNotificationRule),
    addresses: api.addresses.map(transformToNotificationAddress),
  };
}

export function transformToTeamMember(api: ApiTeamMember): TeamMember {
  return {
    id: api.id,
    name: api.name,
    email: api.email,
    isMutedTeamProjects: api.is_muted_team_projects,
  };
}

export function transformToNotificationRuleInput(input: NotificationRuleInput) {
  const selected = input.baseMode === 'selected';
  return {
    base_mode: input.baseMode,
    // Only meaningful for `selected`; the API rejects both under any other mode.
    selected_user_ids: selected ? input.selectedUserIds : [],
    include_creator: selected && input.includeCreator,
    // `off` sends nothing, so the API rejects a copy address alongside it.
    copy_address: input.baseMode === 'off' ? null : input.copyAddress,
  };
}

/** The address a confirmation link is for (and, after confirming, now covers). */
export interface ConfirmedAddress {
  email: string;
}
